import { ChangeDetectionStrategy, Component, input, output, viewChild, ElementRef, effect, DestroyRef, inject, signal, HostListener } from '@angular/core';
import { Router, ActivatedRoute, IsActiveMatchOptions } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Vibration } from 'src/app/bo/vibration';
import { HttpService } from 'src/app/services/http.service';
import { ConfigurationSaisieDonneesAnalyse } from '../../bo/configurationSaisieDonneesAnalyse';
import { DonneeAnalyse } from '../../bo/donneeAnalyse';
import { Dossier } from '../../bo/dossier';
import { Projet } from '../../bo/projet';
import { ProprieteAffichee } from '../../bo/proprieteAffichee';
import { Rotation } from '../../bo/rotation';
import { Tir } from '../../bo/tir';
import { WorkflowEtapeInstance } from '../../bo/workflowEtapeInstance';
import { FonctionsService } from '../../services/fonctions-partagees.service';
import { UtilisateurService } from '../../services/utilisateur.service';
import { FichierExpertabDTO } from '../../bo/fichierExpertab';
import { TranslateService } from '@ngx-translate/core';
import { ControlPoint } from '../../bo/controlPoint';
import { HttpErrorResponse } from '@angular/common/http';
import { AccessPermissionType } from 'src/app/bo/accessPermisionType';
import { ItemSortHelper } from 'src/app/utils/itemSortHelper';
import { ImportModalComponent } from '../import-modal/import-modal.component';
import { KeycloakUtilisateurService } from 'src/app/services/keycloak/keycloak-utilisateur.service';
import { SurveyService } from 'src/app/services/survey.service';
import { VibrationsRecordService } from 'src/app/services/vibrations-record.service';
import { VibrationData } from 'src/app/bo/vibrationsForChart/vibration-data';
import { VibrationsChart } from 'src/app/bo/vibrationsForChart/vibrationsChart';
import { KpiService } from 'src/app/services/kpi.service';
import { Blast, HoleService } from 'src/app/services/hole.service';
import { MatDialog } from '@angular/material/dialog';
import { BlastDrillingData, DrillSessionApiDto, DrillingSession } from 'src/app/shared/models/drilling.models';
import { DEFAULT_SHIFT_CONFIG, DrillActivityService } from 'src/app/services/drill-activity.service';
import { BlastDrillingSessionFormDialogComponent, SessionFormDialogData } from '../blast-drilling-panel/blast-drilling-session-form-dialog.component';


@Component({
  selector: 'app-tir-detail',
  templateUrl: './tir-detail.component.html',
  styleUrls: ['./tir-detail.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TirDetailComponent {
  private readonly destroyRef = inject(DestroyRef);
  
  readonly tir = input.required<Tir>();
  readonly siteId = input.required<number>();
  readonly regionId = input.required<number>();
  readonly projet = input.required<Projet>();
  readonly etape = input<WorkflowEtapeInstance>();
  readonly usePostgresDatabaseForVibrations = input<boolean>();
  readonly showBlastDesignTab = input<boolean>();
  readonly showBlastLoadTab = input<boolean>();
  readonly isSecurityChange = output<WorkflowEtapeInstance>();
  
  readonly tabsWrapper = viewChild<ElementRef>('tabsWrapper');
  readonly tabsList = viewChild<ElementRef>('tabsList');
  
  protected readonly canScrollLeft = signal(false);
  protected readonly canScrollRight = signal(false);
  protected modaleAjout: BsModalRef;
  protected modaleCreateRapport: BsModalRef;
  protected readonly tab = signal(1);
  protected configSaisieSite: ConfigurationSaisieDonneesAnalyse[];
  protected donneesAnalyseTir: DonneeAnalyse[];
  protected readonly initAnalyseFaite = signal(false);
  protected readonly initSuiviFaite = signal(false);
  protected readonly initExpertabFaite = signal(false);
  protected readonly initDossierFaite = signal(false);
  protected readonly initEnvironmentFaite = signal(false);
  protected readonly initVibrationFaite = signal(false);
  protected readonly initCustomerFeedbackFaite = signal(false);
  protected readonly initSupplierFeedbackFaite = signal(false);
  private oldTirId = -1;
  protected tabRotations: Rotation[];
  protected tabVibrations: Vibration[];
  protected propDonneeSuivi: ProprieteAffichee[];
  protected propDonneeVibration: ProprieteAffichee[];
  protected fichierExpertabs: FichierExpertabDTO[];
  protected hauteurListe: number;
  protected dossierRacine: Dossier;
  protected newTabVibrationsChart: VibrationData[] = [];
  protected oldTabVibrationsChart: VibrationsChart[] = [];
  protected tabControlPoints: ControlPoint[] = [];
  protected tabVibrationsOldModel: Vibration[] = [];
  protected surveyJsonForm = signal<any>(null);
  protected supplierSurveyResponses = signal<any>(null);
  protected totalRecords = 0;
  protected importModal: BsModalRef;
  protected customerSurveyResponses = signal<any>(null);
  protected readonly hasReadCustomerSurvey = signal(false);
  protected readonly hasWriteCustomerSurvey = signal(false);
  protected readonly blast = signal<Blast | undefined>(undefined);
  protected readonly hasReadSupplierSurvey = signal(false);
  protected readonly hasWriteSupplierSurvey = signal(false);
  protected currentBlastName = '';
  protected readonly blastDrillingData = signal<BlastDrillingData | null>(null);
  protected readonly initDrillingFaite = signal(false);
  protected readonly shiftConfig = DEFAULT_SHIFT_CONFIG;
  protected readonly activeTabOptions: IsActiveMatchOptions = {
    paths: 'subset',
    queryParams: 'subset',
    fragment: 'ignored',
    matrixParams: 'ignored'
  };

  // Configuration des onglets
  private readonly tabs = {
    1: 'general',
    2: 'analysis',
    3: 'design',
    4: 'load',
    5: 'holes',
    6: 'deliveries',
    7: 'vibrations',
    8: 'fragmentation',
    9: 'load-haul',
    10: 'fleet',
    11: 'files',
    12: 'customer-feedback',
    13: 'supplier-feedback',
    14: 'environment',
    15: 'drilling'
  } as const;

  // Méthodes utilitaires pour la conversion
  private getTabName(tabNumber: number): string {
    return this.tabs[tabNumber] || tabNumber.toString();
  }

  private getTabNumber(tabName: string): number | undefined {
    const entry = Object.entries(this.tabs).find(([_, name]) => name === tabName);
    return entry ? Number(entry[0]) : Number(tabName) || undefined;
  }

  constructor(public utilisateurService: UtilisateurService,
    private readonly http: HttpService, private readonly modalService: BsModalService,
    public router: Router, private readonly route: ActivatedRoute, private readonly fonctionsService: FonctionsService, private readonly translate: TranslateService,
    private readonly surveyService: SurveyService, private readonly vibrationsRecordService: VibrationsRecordService,
    private readonly kpiService: KpiService, private readonly holeService: HoleService,
    private readonly keycloakUtilisateurService: KeycloakUtilisateurService,
    private readonly drillActivityService: DrillActivityService,
    private readonly dialog: MatDialog) {
    
    effect(() => {
      const tir = this.tir();
      const etape = this.etape();
      
      setTimeout(() => {
        if (etape && etape.workflowEtapeInstanceId) {
          this.initDossierFaite.set(false);
          this.changerTab(11);
        } else if (tir && tir.tirId) {
          if (this.oldTirId != tir.tirId) {
            const currentTab = this.tab();
            
            this.initAnalyseFaite.set(false);
            this.initSuiviFaite.set(false);
            this.initVibrationFaite.set(false);
            this.initCustomerFeedbackFaite.set(false);
            this.initSupplierFeedbackFaite.set(false);
            this.initExpertabFaite.set(false);
            this.initDossierFaite.set(false);
            this.initEnvironmentFaite.set(false);
            this.initDrillingFaite.set(false);
            this.blastDrillingData.set(null);
            this.configSaisieSite = [];
            this.donneesAnalyseTir = [];
            this.tabRotations = [];
            this.tabVibrations = [];
            this.customerSurveyResponses.set(null);
            this.supplierSurveyResponses.set(null);
            if (this.isTabAvailable(currentTab)) {
              this.changerTab(currentTab);
            } else {
              this.changerTab(1);
            }
            this.oldTirId = tir.tirId;
          }
        }
      });
      this.getScreenSize();
    });

    effect(() => {
      const tir = this.tir();
      const tirId = tir?.tirId;
      if (this.tab() === 14 && tirId) {
        this.holeService.GetBlastHolePositions(tirId).then((blast: Blast) => {
          this.blast.set(blast);
          this.initEnvironmentFaite.set(true);
        });
      }
    });
  }

  ngOnInit() {
    const userRoles = this.keycloakUtilisateurService.allRoles;
    this.route.queryParams.subscribe(params => {
      const tabParam = params['tab'];
      if (tabParam) {
        const tabNumber = this.getTabNumber(tabParam);
        if (tabNumber && !isNaN(tabNumber) && this.isTabAvailable(tabNumber)) {
          if (this.tab() !== tabNumber) {
            this.changerTab(tabNumber);
          }
        } else {
          this.tab.set(1);
          this.updateUrlWithTab(1);
        }
      } else {
        if (!this.tab() || this.tab() === 0) {
          this.tab.set(1);
        }
        this.updateUrlWithTab(this.tab(), true);
      }
    });

    this.utilisateurService.getBlastedEvent().subscribe(() => {
      this.getCurrentTir();
    })
    this.surveyService.getSurveyQuestionsJsonData(this.regionId()).then((questions) => {
      this.surveyJsonForm.set(questions);
      this.setSurveyRoles(questions, userRoles);
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.checkScrollButtons();
      
      const tabsList = this.tabsList();
      if (tabsList?.nativeElement) {
        tabsList.nativeElement.addEventListener('scroll', () => {
          this.checkScrollButtons();
        });
      }
    }, 100);
  }
  chargerCustomerSurveyResponses() {
    this.surveyService.getSurveyResponsesFromApi(this.tir().tirId).then((responses) => {
      this.customerSurveyResponses.set(responses);
      this.initCustomerFeedbackFaite.set(true);
    })
  }
  chargerSupplierSurveyResponses() {
    this.surveyService.getSurveyResponsesFromApi(this.tir().tirId).then((responses) => {
      this.supplierSurveyResponses.set(responses);
      this.initSupplierFeedbackFaite.set(true);
    })
  }

  setSurveyRoles(jsonForm: any, userRoles: String[]) {
    if (jsonForm) {
      this.hasReadCustomerSurvey.set(jsonForm.customerSurvey?.permission?.read.some(r => userRoles.includes(r)));
      this.hasWriteCustomerSurvey.set(jsonForm.customerSurvey?.permission?.write.some(r => userRoles.includes(r)));

      this.hasReadSupplierSurvey.set(jsonForm.providerSurvey?.permission?.read.some(r => userRoles.includes(r)));
      this.hasWriteSupplierSurvey.set(jsonForm.providerSurvey?.permission?.write.some(r => userRoles.includes(r)));
    }
  }


  ngOnChanges() {
    // Removed - logic moved to effect in constructor
  }

  checkScrollButtons() {
    const tabsList = this.tabsList();
    if (tabsList?.nativeElement) {
      const element = tabsList.nativeElement;
      this.canScrollLeft.set(element.scrollLeft > 0);
      this.canScrollRight.set(element.scrollLeft < (element.scrollWidth - element.clientWidth));
    }
  }

  scrollTabs(direction: 'left' | 'right') {
    const tabsList = this.tabsList();
    if (tabsList?.nativeElement) {
      const element = tabsList.nativeElement;
      const scrollAmount = 200;
      
      if (direction === 'left') {
        element.scrollLeft -= scrollAmount;
      } else {
        element.scrollLeft += scrollAmount;
      }
      
      setTimeout(() => {
        this.checkScrollButtons();
      }, 300);
    }
  }

  // si la fenêtre change de taille
  @HostListener('window:resize', ['$event'])
  getScreenSize(event?) {
    this.hauteurListe = 100;
    setTimeout(() => {
      this.checkScrollButtons();
    }, 100);
  }

  changerTab(numTab: number) {
    if (numTab == 2 && !this.initAnalyseFaite()) {
      this.chargerDonneesAnalyse();
      this.tab.set(2);
    } else if (numTab == 9 && !this.initSuiviFaite()) {
      this.chargerDonneesSuivi();
      this.tab.set(9);
    } else if (numTab == 11 && !this.initDossierFaite()) {
      this.chargerDossier();
      this.tab.set(11);
    } else if (numTab == 7 && !this.initVibrationFaite()) {
      if( this.usePostgresDatabaseForVibrations()) {
        this.chargerNewVibrations();
      }else{
        this.chargerVibrations();
      }
      this.tab.set(7);
    } else if (numTab == 5) {
      this.tab.set(5);
    }
    else if (numTab == 12 && !this.initCustomerFeedbackFaite()) {
      this.chargerCustomerSurveyResponses();
      this.tab.set(12);
    }
    else if (numTab == 13 && !this.initSupplierFeedbackFaite()) {
      this.chargerSupplierSurveyResponses();
      this.tab.set(13);
    }
    else if (numTab == 10 && !this.initExpertabFaite() && this.utilisateurService.currentSite()?.siteId == 189) {
      this.currentBlastName = this.tir()?.nom ?? '';
      this.tab.set(10);
    }
    else if (numTab == 8) {
      this.tab.set(8);
    }
    else if (numTab == 3) {
      if(this.showBlastDesignTab()){
        this.getBlastDesign();
        this.tab.set(3);
      }
    }
    else if (numTab == 4) {
      if(this.showBlastLoadTab()){
        this.getBlastLoading();
        this.tab.set(4);
      }
    }
    else if (numTab == 14) {
      this.tab.set(14);
    }
    else if (numTab == 15 && !this.initDrillingFaite()) {
      this.chargerDrillingSessions();
      this.tab.set(15);
    }
    else
      this.tab.set(numTab);
    this.updateUrlWithTab(this.tab());
    
    setTimeout(() => {
      this.scrollToActiveTab();
    }, 100);
  }

  scrollToActiveTab() {
    const tabsList = this.tabsList();
    if (tabsList && tabsList.nativeElement) {
      const element = tabsList.nativeElement;
      const activeTab = element.querySelector('.nav-item.active');
      
      if (activeTab) {
        activeTab.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
        
        setTimeout(() => {
          this.checkScrollButtons();
        }, 300);
      }
    }
  }
    getBlastLoading() {
      this.holeService.GetBlastLoading(this.tir().tirId).then((blast: Blast) => {
      this.blast.set(blast);

      });
    }

    getBlastDesign() {
      this.holeService.GetBlastDesign(this.tir().tirId).then((blast: Blast) => {
      this.blast.set(blast);

      });
    }

  chargerVibrations() {
    this.fonctionsService.loading_screen(true);
    const controlPointsData = this.http.get<ControlPoint[]>('Sites/' + this.utilisateurService.currentSite()?.siteId + '/ControlPoints', { transformCoord: false });
    const vibrationsMongoData = this.http.get<VibrationsChart[]>('VibrationsMongo/' + this.tir().tirId + '/GetAllByTir')
    // const vobrationsOldModelData = this.http.get<Vibration[]>('Vibrations/' + this.tir.tirId + '/GetAllByTir')

    forkJoin([vibrationsMongoData, controlPointsData]).subscribe(resultats => {
      this.propDonneeVibration = new Vibration().getProprietesAffichees();
      this.oldTirId = this.tir().tirId;
      this.oldTabVibrationsChart = resultats[0];
      this.tabControlPoints = resultats[1];

      this.http.get<Vibration[]>('Vibrations/' + this.tir().tirId + '/GetAllByTir').subscribe({
        next: (response) => {
          this.tabVibrationsOldModel = response;
          this.initVibrationFaite.set(true);
          this.fonctionsService.loading_screen(false);
        }
      })

    }, () => {
      this.translate.get('GLOBAL.NO_ITEM_FOUND').subscribe(trad => {
        this.fonctionsService.launch_toast(false, false, trad)
        this.fonctionsService.loading_screen(false);
      });
      this.initVibrationFaite.set(true);
      this.fonctionsService.loading_screen(false);
    });
  }
  chargerNewVibrations() {
    this.fonctionsService.loading_screen(true);
    const controlPoints$ = this.vibrationsRecordService.loadControlPoints(this.siteId());
    const vibrationRecords$ = this.vibrationsRecordService.GetRecords(
      this.utilisateurService.currentSite()?.siteId,
      null,
      this.tir().tirId,
      null,
      1,
      30,
      false,false,false
    );
    forkJoin({ vibrationRecords: vibrationRecords$, controlPoints: controlPoints$ })
      .subscribe({
        next: ({ vibrationRecords, controlPoints }) => {
          this.propDonneeVibration = new Vibration().getProprietesAffichees();
          this.oldTirId = this.tir()?.tirId ?? null;
          this.newTabVibrationsChart = vibrationRecords.data.records;
          this.totalRecords = vibrationRecords.data.total;
          this.tabControlPoints = controlPoints;
          this.initVibrationFaite.set(true);
          this.fonctionsService.loading_screen(false);
        },
        error: (err) => {
          this.translate.get('GLOBAL.NO_ITEM_FOUND').subscribe((trad) => {
            this.fonctionsService.launch_toast(false, false, trad);
          });
          this.initVibrationFaite.set(true);
          this.fonctionsService.loading_screen(false);
          console.error('Error loading vibrations:', err);
        }
      });
  }

  getCurrentTir() {
    this.http.get<Tir>('Blasts/' + this.tir().tirId).subscribe((res: Tir) => {
      // Note: Cannot set input signal directly, would need to emit to parent
      console.info(res)
    })
  }

  chargerDonneesAnalyse() {
    var startLoader = setTimeout(() => {
      this.fonctionsService.loading_screen(true);
    }, 300);
    let donneesAnalyseProjet = this.kpiService.getBlastAnalysisData(this.tir().tirId);
    let configSaisieSite = this.kpiService.getSiteConfigAnalysisEntry(this.siteId());

    const isGuest = !this.keycloakUtilisateurService.allRoles.includes('access_all_kpi');

    forkJoin([donneesAnalyseProjet, configSaisieSite]).subscribe(async resultats => {


      resultats[0].forEach(donneeAnal => {
        let config = resultats[1].find(o => o.referentielDonneeAnalyseId === donneeAnal.referentielDonneeAnalyseId);

        if (config) {
          donneeAnal.accessPermission = config.accessPermission;
          donneeAnal.isEnabled = config.isEnabled;

          donneeAnal.autorisePourUtil = (isGuest && config.accessPermission === AccessPermissionType.READANDWRITE) || (!isGuest && config.isEnabled);
        }
      });

      resultats[0].sort((a, b) => {
        if (a.autorisePourUtil && b.autorisePourUtil || !a.autorisePourUtil && !b.autorisePourUtil)
          return a.referentielDonneeAnalyse.nom.localeCompare(b.referentielDonneeAnalyse.nom);
        else if (a.autorisePourUtil && !b.autorisePourUtil)
          return -1;
        else if (!a.autorisePourUtil && b.autorisePourUtil)
          return 1;
      })

      try {
        resultats[0] = await ItemSortHelper.sortKpi(resultats[0]);
      } catch (error) { }

      this.fonctionsService.loading_screen(false);
      clearTimeout(startLoader);
      this.initAnalyseFaite.set(true);
      this.oldTirId = this.tir().tirId;
      this.donneesAnalyseTir = resultats[0];
      this.configSaisieSite = resultats[1];

      var id = this.utilisateurService.currentProjet.tirs.findIndex(p => p.tirId == this.tir().tirId);
      let donneeNonRenseigne = false;
      this.donneesAnalyseTir.forEach(donnee => {
        if (donnee.valeur === '' || donnee.donneeAnalyseId === -1)
          donneeNonRenseigne = true;
      });
      this.utilisateurService.currentProjet.tirs[id].donneeAnalyseRenseignees = !donneeNonRenseigne && this.donneesAnalyseTir.length > 0;
    }, () => {
      this.fonctionsService.loading_screen(false);
      clearTimeout(startLoader);
    });
  }


  chargerDonneesSuivi() {
    var startLoader = setTimeout(() => {
      this.fonctionsService.loading_screen(true);
    }, 300);
    this.propDonneeSuivi = new Rotation().getProprietesAffichees(false);

    this.http.get<Rotation[]>('Blasts/' + this.tir().tirId + '/Rotations').subscribe(rotations => {
      this.tabRotations = rotations;
      this.initSuiviFaite.set(true);
      this.oldTirId = this.tir().tirId;
      this.fonctionsService.loading_screen(false);
      clearTimeout(startLoader);
    }, () => {
      this.fonctionsService.loading_screen(false);
      clearTimeout(startLoader);
    });
  }


  chargerDossier() {
    const etape = this.etape();
    if (etape == null || etape.workflowEtapeInstanceId == null || etape === undefined) return;

    var startLoader = setTimeout(() => {
      this.fonctionsService.loading_screen(true);
    }, 300);
    this.http.get<Dossier>('WorkflowSteps/' + etape.workflowEtapeInstanceId + '/Folder').subscribe(dossier => {
      console.log(`Charged dossier for workflow step ${etape.workflowEtapeInstanceId}:`, dossier);
      
      this.dossierRacine = dossier;
      this.initDossierFaite.set(true);
      this.fonctionsService.loading_screen(false);
      clearTimeout(startLoader);
    }, (error: HttpErrorResponse) => {
      if (error.status === 401) {
        this.translate.get('GLOBAL.NON_AUTORISE').subscribe(trad => {
          this.fonctionsService.launch_toast(false, false, trad)
          this.fonctionsService.loading_screen(false);
        });
      }
      this.fonctionsService.loading_screen(false);
      clearTimeout(startLoader);
    });
  }

  chargerDrillingSessions() {
    const blastId = this.tir().tirId;
    forkJoin([
      this.drillActivityService.fetchSessionsByBlastId(blastId).pipe(catchError(() => of([]))),
      this.drillActivityService.fetchBlastKpi(blastId),
    ]).subscribe(([sessions, kpi]) => {
      this.blastDrillingData.set(
        this.drillActivityService.buildBlastDrillingData(this.tir(), sessions, kpi.holeCount, kpi.plannedLength, undefined, this.utilisateurService.currentSite()?.timezone)
      );
      this.initDrillingFaite.set(true);
    });
  }

  private reloadDrillingSessions(): void {
    const blastId = this.tir().tirId;
    forkJoin([
      this.drillActivityService.fetchSessionsByBlastId(blastId).pipe(catchError(() => of([]))),
      this.drillActivityService.fetchBlastKpi(blastId),
    ]).subscribe(([sessions, kpi]) => {
      this.blastDrillingData.set(
        this.drillActivityService.buildBlastDrillingData(this.tir(), sessions, kpi.holeCount, kpi.plannedLength, undefined, this.utilisateurService.currentSite()?.timezone)
      );
    });
  }

  onDrillingAddSession(): void {
    const dialogData: SessionFormDialogData = { blastId: this.tir().tirId, siteId: this.siteId(), timezone: this.utilisateurService.currentSite()?.timezone };
    this.dialog.open(BlastDrillingSessionFormDialogComponent, { data: dialogData })
      .afterClosed()
      .subscribe((dto: DrillSessionApiDto | undefined) => {
        if (!dto) return;
        this.http.post<DrillSessionApiDto, DrillSessionApiDto>('DrillSessions', dto)
          .subscribe({ next: () => this.reloadDrillingSessions() });
      });
  }

  onDrillingEditSession(session: DrillingSession): void {
    const dialogData: SessionFormDialogData = { blastId: this.tir().tirId, siteId: this.siteId(), session: session.rawDto, timezone: this.utilisateurService.currentSite()?.timezone };
    this.dialog.open(BlastDrillingSessionFormDialogComponent, { data: dialogData })
      .afterClosed()
      .subscribe((dto: DrillSessionApiDto | undefined) => {
        if (!dto) return;
        this.http.put<DrillSessionApiDto, DrillSessionApiDto>('DrillSessions', dto)
          .subscribe({ next: () => this.reloadDrillingSessions() });
      });
  }

  onDrillingDeleteSession(session: DrillingSession): void {
    console.warn('Delete drilling session not yet supported — no API endpoint', session.id);
  }

  chargerFichierExpertabs() {
    ///TODO: design a new history log

    // var startLoader = setTimeout(() => {
    //   this.fonctionsService.loading_screen(true);
    // }, 300);
    // this.http.get<FichierExpertabDTO[]>('Blasts/' + this.tir.tirId + '/Expertab/All').subscribe(fichierExpertirs => {
    //   this.fichierExpertabs = fichierExpertirs;
    //   this.initExpertabFaite = true;
    //   this.fonctionsService.loading_screen(false);
    //   clearTimeout(startLoader);
    // }, () => {
    //   this.fonctionsService.loading_screen(false);
    //   clearTimeout(startLoader);
    // });
  }


  modifDossier(d: Dossier) {
    this.dossierRacine = d;
    const etape = this.etape();
    if (!etape) return;
    
    let index = this.utilisateurService.currentTir.workflowInstance.workflowEtapeInstances.findIndex(o => o.workflowEtapeInstanceId == etape.workflowEtapeInstanceId);
    this.utilisateurService.currentTir.workflowInstance.workflowEtapeInstances[index].estRenseignee = this.detecterFichier(d);
  }

  detecterFichier(dossier: Dossier) {
    if (dossier.fichiers && dossier.fichiers.length > 0)
      return true;
    else if (dossier.sousDossiers && dossier.sousDossiers.length > 0) {
      let i = 0;
      let resultat = false;
      while (!resultat && i < dossier.sousDossiers.length) {
        resultat = this.detecterFichier(dossier.sousDossiers[i]);
        i++;
      }
      return resultat;
    }
    return false;
  }

  openFileSelector() {
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    fileInput.click();
  }

  protected securityIsChange(event: WorkflowEtapeInstance) {
    this.isSecurityChange.emit(event);
  }


  openImportModal() {
    var initialState = {
      uploadUrl: 'Blasts/' + this.tir().tirId + '/blastFromStr',
      allowStrFileImport: true
    };
    this.importModal = this.modalService.show(ImportModalComponent, { backdrop: true, ignoreBackdropClick: true, keyboard: false, initialState, class: 'modal-dialog-centered' });
    const subscription = this.modalService.onHide.subscribe({
      next: () => {
        subscription.unsubscribe();
      },
      error: (error) => {
        console.error('Error with modal subscription:', error);
      },
      complete: () => {
        subscription.unsubscribe();
      }
    });
  }

  private updateUrlWithTab(tabNumber: number, replaceUrl = false) {
    const tabName = this.getTabName(tabNumber);
    
    if (replaceUrl) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { tab: tabName },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    } else {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { tab: tabName },
        queryParamsHandling: 'merge'
      });
    }
  }

  protected isTabAvailable(tabNumber: number): boolean {
    const features = this.utilisateurService.currentSite()?.features;
    const isStep = this.router.url.includes('step');
    switch (tabNumber) {
      case 1:
      case 2:
        return !isStep;
      case 3:
        return !isStep && (features?.showBlastDesignTab ?? this.showBlastDesignTab() ?? true);
      case 4:
        return !isStep && (features?.showBlastLoadTab ?? this.showBlastLoadTab() ?? true);
      case 5:
        return !isStep && (features?.show3dViewerTab ?? true);
      case 6:
        return !isStep && (features?.showDeliveriesTab ?? true);
      case 7:
        return !isStep && (features?.showVibrationsTab ?? true);
      case 8:
        return !isStep && (features?.showFragmentationTab ?? true);
      case 9:
        return !isStep && (features?.showLoadAndHaulTab ?? false);
      case 10:
        return !isStep && (features?.showFleetManagementSystemTab ?? false);
      case 11:
        return isStep;
      case 12:
        return !isStep && (this.hasReadCustomerSurvey() || this.hasWriteCustomerSurvey());
      case 13:
        return !isStep && (this.hasReadSupplierSurvey() || this.hasWriteSupplierSurvey());
      case 14:
        return !isStep && (features?.showEnvironmentTab ?? true);
      default:
        return !isStep;
    }
  }

}
